<template>
  <div>
    <v-card class="mx-auto" max-width="800" elevation="3">
      <!-- Encabezado minimalista -->
      <v-app-bar 
        color="yellow darken-1" 
        dark 
        flat 
        dense
        height="56"
      >
        <v-icon class="mr-3">{{ esPagoAnticipado ? 'mdi-clock-plus' : 'mdi-cash-plus' }}</v-icon>
        <v-toolbar-title class="subtitle-1 font-weight-medium">
          {{ esPagoAnticipado ? 'Pago Anticipado' : 'Realizar Pago' }}
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon @click="cerrarDialogo">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-app-bar>
      
      <v-card-text class="pa-4">
        <!-- Info del miembro en una sola línea -->
        <div class="d-flex align-center pa-3 mb-4 grey lighten-4 rounded">
          <v-avatar size="32" color="primary" class="mr-3">
            <span class="white--text font-weight-bold">
              {{ nombreMiembro ? nombreMiembro.charAt(0).toUpperCase() : 'M' }}
            </span>
          </v-avatar>
          <div class="flex-grow-1">
            <span class="font-weight-bold">{{ nombreMiembro || 'Miembro' }}</span>
            <span class="ml-2 grey--text">
              <v-icon small class="mr-1">mdi-calendar</v-icon>
              {{ membresiaActual && membresiaActual.fechaFin ? 
                 'Vence: ' + formatearFecha(membresiaActual.fechaFin) : 
                 'Matrícula: ' + matricula }}
            </span>
          </div>
          <v-chip 
            v-if="membresiaActual && membresiaActual.estado"
            :color="membresiaActual.estado === 'ACTIVO' ? 'success' : 
                   membresiaActual.estado === 'PENDIENTE' ? 'warning' : 'error'"
            small
            outlined
          >
            {{ membresiaActual.estado }}
          </v-chip>
        </div>

        <!-- Formulario en grid simple -->
        <v-container fluid class="pa-0">
          <v-row>
            <!-- Información de pago integrada -->
            <v-col cols="12" v-if="infoPago && !esPagoAnticipado">
              <div class="d-flex justify-space-between align-center pa-3 blue-grey lighten-5 rounded mb-3">
                <div class="d-flex">
                  <div class="mr-4">
                    <div class="caption grey--text">Precio</div>
                    <div class="body-2 font-weight-bold">${{ infoPago.precio.toLocaleString() }}</div>
                  </div>
                  <div class="mr-4">
                    <div class="caption grey--text">Pagado</div>
                    <div class="body-2">${{ infoPago.pagado.toLocaleString() }}</div>
                  </div>
                </div>
                <div v-if="infoPago.restante > 0">
                  <div class="caption grey--text">Pendiente</div>
                  <div class="title orange--text font-weight-bold">${{ infoPago.restante.toLocaleString() }}</div>
                </div>
                <v-chip v-else color="success" small>Completo</v-chip>
              </div>
            </v-col>

            <!-- Pago anticipado info -->
            <v-col cols="12" v-if="esPagoAnticipado">
              <v-alert type="info" dense class="mb-3">
                <v-icon left>mdi-clock-outline</v-icon>
                Este pago se activará automáticamente cuando termine la membresía actual
              </v-alert>
            </v-col>

            <!-- Campos del formulario en grid -->
            <v-col cols="12" md="6">
              <v-select
                v-model="membresiaSeleccionada"
                :items="membresias"
                item-text="nombre"
                item-value="id"
                label="Membresía"
                return-object
                @change="consultarInfoPago"
                outlined
                hide-details
              >
                <template v-slot:prepend-inner>
                  <v-icon color="grey darken-2">mdi-card-membership</v-icon>
                </template>
                <template v-slot:item="{ item }">
                  <div>
                    <div class="font-weight-medium">{{ item.nombre }}</div>
                    <div class="caption grey--text">${{ item.precio.toLocaleString() }} • {{ item.duracion }} días</div>
                  </div>
                </template>
              </v-select>
            </v-col>

            <v-col cols="12" md="6">
              <v-menu
                v-model="menuFecha"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="fechaSeleccionadaFormatted"
                    label="Fecha de inicio"
                    prepend-inner-icon="mdi-calendar"
                    readonly
                    outlined
                    hide-details
                    v-bind="attrs"
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="fechaSeleccionada"
                  :first-day-of-week="1"
                  locale="es"
                  @input="menuFecha = false"
                  color="yellow darken-1"
                ></v-date-picker>
              </v-menu>
            </v-col>

            <v-col cols="12" md="6">
              <v-select
                v-model="metodoPago"
                :items="metodosPago"
                label="Método de pago"
                outlined
                hide-details
                prepend-inner-icon="mdi-wallet"
              ></v-select>
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field
                v-model="montoPago"
                label="Monto a pagar"
                type="number"
                min="1"
                :max="membresiaSeleccionada ? membresiaSeleccionada.precio : 1"
                :disabled="!membresiaSeleccionada"
                prefix="$"
                outlined
                hide-details
                prepend-inner-icon="mdi-cash"
              ></v-text-field>
            </v-col>

            <!-- Alerta de abono si aplica -->
            <v-col cols="12" v-if="esPagoParci()">
              <v-alert type="warning" dense outlined>
                <v-icon left small>mdi-information</v-icon>
                Abono: Las fechas de membresía se mantendrán
              </v-alert>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>
      
      <!-- Botones limpios -->
      <v-card-actions class="pa-4 pt-0">
        <v-spacer></v-spacer>
        <v-btn 
          text 
          @click="cerrarDialogo"
          class="mr-2"
        >
          Cancelar
        </v-btn>
        <v-btn
          color="yellow darken-1"
          class="black--text font-weight-medium px-6"
          @click="mostrarPantallaConfirmacion"
          :disabled="!membresiaSeleccionada || !montoPago || procesandoPago"
          depressed
        >
          <v-icon left>mdi-check</v-icon>
          Revisar Pago
        </v-btn>
      </v-card-actions>

      <!-- Overlay de carga -->
      <v-overlay :value="cargando">
        <v-progress-circular indeterminate size="64" color="white"></v-progress-circular>
      </v-overlay>
    </v-card>

    <!-- Mensaje de éxito simple -->
    <v-snackbar
      v-model="mostrarMensajeExito"
      color="success"
      :timeout="3000"
      top
    >
      <v-icon left>mdi-check-circle</v-icon>
      Pago realizado exitosamente
    </v-snackbar>

    <!-- Diálogo de Confirmación -->
    <v-dialog v-model="mostrarConfirmacion" persistent max-width="550">
      <v-card class="elevation-12">
        <!-- Header moderno -->
        <v-app-bar
          color="yellow darken-1"
          dark
          dense
          elevation="0"
          style="border-radius: 4px 4px 0 0;"
        >
          <v-icon class="mr-2">{{ esPagoAnticipado ? 'mdi-clock-check' : 'mdi-cash-check' }}</v-icon>
          <v-toolbar-title class="text-h6">
            {{ esPagoAnticipado ? 'Pago Anticipado' : 'Confirmar Pago' }}
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-chip :color="esPagoAnticipado ? 'orange' : 'green'" small text-color="white">
            <v-icon left small>{{ esPagoAnticipado ? 'mdi-clock-outline' : 'mdi-cash' }}</v-icon>
            {{ esPagoAnticipado ? 'Anticipado' : 'Regular' }}
          </v-chip>
        </v-app-bar>
        
        <v-card-text class="pa-4">
          <!-- Información del miembro compacta -->
          <div class="d-flex align-center mb-4 pa-3 yellow lighten-4 rounded">
            <v-avatar color="yellow darken-1" size="36" class="mr-3">
              <v-icon color="white" size="20">mdi-account</v-icon>
            </v-avatar>
            <div>
              <div class="font-weight-bold text-subtitle-1">{{ nombreMiembro || 'Miembro' }}</div>
              <div class="caption text--secondary" v-if="membresiaActual && membresiaActual.fechaFin">
                Vence: {{ formatearFecha(membresiaActual.fechaFin) }}
              </div>
              <div class="caption text--secondary" v-else>
                Matrícula: {{ matricula }}
              </div>
            </div>
          </div>

          <!-- Resumen del pago en grid compacto -->
          <v-row dense class="mb-3">
            <v-col cols="6">
              <div class="text-caption text--secondary">Membresía</div>
              <div class="font-weight-bold">{{ membresiaSeleccionada ? membresiaSeleccionada.nombre : '' }}</div>
            </v-col>
            <v-col cols="6">
              <div class="text-caption text--secondary">Duración</div>
              <div class="font-weight-bold">{{ membresiaSeleccionada ? membresiaSeleccionada.duracion : '' }} días</div>
            </v-col>
          </v-row>

          <v-row dense class="mb-3">
            <v-col cols="6">
              <div class="text-caption text--secondary">Método</div>
              <v-chip small :color="metodoPago === 'efectivo' ? 'green' : 'blue'" text-color="white">
                <v-icon left size="14">{{ metodoPago === 'efectivo' ? 'mdi-cash' : 'mdi-credit-card' }}</v-icon>
                {{ metodoPago }}
              </v-chip>
            </v-col>
            <v-col cols="6">
              <div class="text-caption text--secondary">Monto</div>
              <div class="font-weight-bold text-h6 yellow--text text--darken-2">${{ Number(montoPago).toLocaleString() }}</div>
            </v-col>
          </v-row>

          <!-- Fechas solo si no es pago anticipado -->
          <v-row dense class="mb-3" v-if="!esPagoAnticipado">
            <v-col cols="6">
              <div class="text-caption text--secondary">Fecha inicio</div>
              <div class="font-weight-medium">{{ obtenerInfoFechas().fechaInicio }}</div>
            </v-col>
            <v-col cols="6">
              <div class="text-caption text--secondary">Fecha fin</div>
              <div class="font-weight-medium">{{ obtenerInfoFechas().fechaFin }}</div>
            </v-col>
          </v-row>
          
          <!-- Estado del pago si aplica -->
          <v-alert
            v-if="infoPago && !esPagoAnticipado"
            border="left"
            color="orange"
            dense
            text
            class="mb-3"
          >
            <div class="text-caption">Estado actual del pago</div>
            <div class="font-weight-bold">
              ${{ infoPago.pagado.toLocaleString() }} de ${{ infoPago.precio.toLocaleString() }}
              <span v-if="infoPago.restante > 0" class="orange--text">
                (Resta: ${{ infoPago.restante.toLocaleString() }})
              </span>
            </div>
          </v-alert>

          <!-- Nota informativa -->
          <v-alert
            v-if="!esPagoAnticipado"
            border="left"
            color="info"
            dense
            text
            icon="mdi-information-outline"
          >
            Una vez confirmado, el pago se procesará inmediatamente
          </v-alert>
        </v-card-text>
        
        <v-divider></v-divider>
        
        <v-card-actions class="pa-4">
          <v-btn 
            color="grey" 
            text 
            @click="cancelarConfirmacion"
            :disabled="procesandoPago"
          >
            <v-icon left small>mdi-close</v-icon>
            Cancelar
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="success"
            @click="realizarPago"
            :disabled="procesandoPago"
            :loading="cargando"
            elevation="2"
          >
            <v-icon left small>mdi-check-bold</v-icon>
            Confirmar Pago
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
<script>
import HttpService from '../../Servicios/HttpService'
import Miembros from './Miembros.vue';
export default {
  name: "RealizarPago",
  props: ["matricula", "membresiaActual", "nombreMiembro", "esPagoAnticipado"],

  data:()=>({
    membresiaSeleccionada: null,
    fechaSeleccionada: new Date().toISOString().split('T')[0], // Formato correcto YYYY-MM-DD
    menuFecha: false, // Nuevo estado para el menú de fecha
    membresias: [],
    cargando: false,
    metodoPago: 'efectivo',
    metodosPago: [
      { text: 'Efectivo', value: 'efectivo' },
      { text: 'Transferencia', value: 'transferencia' }
    ],
    infoPago: null,
    montoPago: '',
    mostrarConfirmacion: false,
    procesandoPago: false,
    mostrarMensajeExito: false
  }),

  computed: {
    // Computed property para formatear la fecha seleccionada
    fechaSeleccionadaFormatted() {
      if (!this.fechaSeleccionada) return '';
      
      const fecha = new Date(this.fechaSeleccionada + 'T00:00:00');
      return fecha.toLocaleDateString('es-ES', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }
  },

  watch: {
    matricula: {
      handler: function (newVal, oldVal) {
        // Limpiar todos los campos al cambiar de miembro
        this.membresiaSeleccionada = null;
        this.montoPago = '';
        this.infoPago = null;
        this.metodoPago = 'efectivo';
        this.fechaSeleccionada = new Date().toISOString().split('T')[0]; // Formato correcto
        this.mostrarConfirmacion = false;
        this.procesandoPago = false;
        
        // Si el componente ya está montado y hay membresías cargadas, autocompletar
        if (newVal && this.membresias.length > 0) {
          this.autocompletarCamposPendientes();
        }
      },
      immediate: true
    },
    
    // Observar cambios en membresiaActual para autocompletar cuando cambie
    membresiaActual: {
      handler: function(newVal, oldVal) {
        if (newVal && this.membresias.length > 0) {
          this.autocompletarCamposPendientes();
        }
      },
      deep: true,
      immediate: true
    }
  },

  mounted(){
    this.obtenerMembresias();
  },

  methods:{
    obtenerMembresias() {
      const payload = { metodo: "get" };
      HttpService.obtenerConDatos(payload, "membresias.php").then(
        (respuesta) => {
          this.membresias = respuesta;
          // Autocompletar campos cuando se cargan las membresías
          this.autocompletarCamposPendientes();
        }
      );
    },

    autocompletarCamposPendientes() {
      // Solo autocompletar si hay membresía actual y está en estado que requiere pago
      if (!this.membresiaActual || !this.membresiaActual.idMembresia) {
        return;
      }

      // Estados que requieren autocompletado: PENDIENTE, ACTIVO (para abonos)
      const estadosAutocompletar = ['PENDIENTE', 'ACTIVO'];
      if (!estadosAutocompletar.includes(this.membresiaActual.estado)) {
        return;
      }

      // Buscar la membresía correspondiente en la lista
      const membresiaEncontrada = this.membresias.find(m => m.id == this.membresiaActual.idMembresia);
      if (!membresiaEncontrada) {
        return;
      }

      // Seleccionar la membresía automáticamente
      this.membresiaSeleccionada = membresiaEncontrada;
      
      // Consultar información del pago para obtener el monto restante
      this.consultarInfoPago();
    },

    cerrarDialogo(){
      this.$emit("cerrar", false)
    },

    realizarPago(){
      // Prevenir pagos duplicados
      if(this.procesandoPago) {
        return;
      }

      if(!this.membresiaSeleccionada) {
        return;
      }

      this.procesandoPago = true;
      this.cargando = true;

      // Obtener idUsuario y validar
      let idUsuario = localStorage.getItem('idUsuario')
      if (!idUsuario) {
        this.procesandoPago = false;
        this.cargando = false
        return
      }
      
      let payload = {
        metodo: this.esPagoAnticipado ? 'pago_anticipado' : 'pagar',
        pago: {
          matricula: this.matricula,
          pago: this.montoPago || this.membresiaSeleccionada.precio,
          idMembresia: this.membresiaSeleccionada.id,
          duracion: this.membresiaSeleccionada.duracion,
          fecha: this.fechaSeleccionada,
          idUsuario: idUsuario,
          metodo_pago: this.metodoPago,
          monto_pagado: this.montoPago || this.membresiaSeleccionada.precio,
          fecha_pago: this.fechaSeleccionada
        }
      }

      // Combinar la fecha seleccionada con la hora actual
      const now = new Date();
      const hora = now.getHours().toString().padStart(2, '0');
      const minutos = now.getMinutes().toString().padStart(2, '0');
      const segundos = now.getSeconds().toString().padStart(2, '0');
      payload.pago.fecha = `${this.fechaSeleccionada} ${hora}:${minutos}:${segundos}`;

      HttpService.registrar(payload,"miembros.php")
      .then(registrado => {
        if(registrado){
          this.procesandoPago = false;
          this.cargando = false;
          this.mostrarConfirmacion = false;
          
          // Emitir evento de pago exitoso sin mostrar alert
          this.$emit("pagado", registrado)
        }
      })
      .catch(error => {
        this.procesandoPago = false;
        this.cargando = false
      })
    },

    consultarInfoPago() {
      if (!this.membresiaSeleccionada || !this.membresiaSeleccionada.id) {
        this.infoPago = null;
        this.montoPago = '';
        return;
      }
      const payload = {
        metodo: 'info_pago_miembro',
        matricula: this.matricula,
        idMembresia: this.membresiaSeleccionada.id
      };
      HttpService.obtenerConDatos(payload, 'miembros.php').then((respuesta) => {
        this.infoPago = respuesta;
        
        // Establecer el monto según el estado del pago
        if (respuesta.restante > 0) {
          // Si hay saldo pendiente, usar el monto restante
          this.montoPago = respuesta.restante;
        } else {
          // Si está completamente pagado o es nueva membresía, usar precio completo
          this.montoPago = this.membresiaSeleccionada.precio;
        }
      }).catch(error => {
        // En caso de error, usar el precio completo de la membresía
        this.montoPago = this.membresiaSeleccionada.precio;
      });
    },

    formatearFecha(fecha) {
      return new Date(fecha).toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    },

    calcularFechaFinalizacion() {
      if (!this.membresiaSeleccionada) return '';
      
      // Si es un pago parcial/abono (ya tiene membresía activa con fechas), usar las fechas existentes
      if (this.esPagoParci()) {
        if (this.membresiaActual && this.membresiaActual.fechaFin) {
          return this.formatearFecha(this.membresiaActual.fechaFin);
        }
      }
      
      // Si es un nuevo ciclo o primera vez, usar la fecha actual como base
      const fechaActual = new Date(); // Usar fecha actual del sistema
      const fechaFin = new Date(fechaActual);
      fechaFin.setDate(fechaFin.getDate() + this.membresiaSeleccionada.duracion);
      
      return this.formatearFecha(fechaFin);
    },

    esPagoParci() {
      // Es pago parcial si:
      // 1. Ya tiene una membresía activa o pendiente
      // 2. La membresía seleccionada es la misma que la actual
      // 3. Aún no está vencida
      if (!this.membresiaActual || !this.infoPago) return false;
      
      const estadosValidos = ['ACTIVO', 'PENDIENTE'];
      const mismaMembresía = this.membresiaActual.idMembresia == (this.membresiaSeleccionada && this.membresiaSeleccionada.id);
      const tieneEstadoValido = estadosValidos.includes(this.membresiaActual.estado);
      const tieneSaldoPendiente = this.infoPago.restante > 0;
      
      return mismaMembresía && tieneEstadoValido && tieneSaldoPendiente;
    },

    obtenerInfoFechas() {
      // Retorna información sobre las fechas que se usarán
      if (this.esPagoParci()) {
        return {
          fechaInicio: this.membresiaActual.fechaInicio ? this.formatearFecha(this.membresiaActual.fechaInicio) : 'Fecha existente',
          fechaFin: this.membresiaActual.fechaFin ? this.formatearFecha(this.membresiaActual.fechaFin) : 'Fecha existente',
          esParcial: true
        };
      } else {
        // Para nuevos ciclos, usar la fecha seleccionada por el usuario
        const fechaInicio = new Date(this.fechaSeleccionada + 'T00:00:00'); // Usar la fecha seleccionada
        const fechaFin = new Date(fechaInicio);
        const duracion = this.membresiaSeleccionada && this.membresiaSeleccionada.duracion ? this.membresiaSeleccionada.duracion : 0;
        fechaFin.setDate(fechaFin.getDate() + duracion - 1);
        
        return {
          fechaInicio: this.formatearFecha(fechaInicio),
          fechaFin: this.formatearFecha(fechaFin),
          esParcial: false
        };
      }
    },

    mostrarPantallaConfirmacion() {
      if(!this.membresiaSeleccionada) {
        return;
      }
      if(!this.montoPago) {
        return;
      }
      
      this.mostrarConfirmacion = true;
    },

    cancelarConfirmacion() {
      this.mostrarConfirmacion = false;
    }
  }
};
</script>


// WEBPACK FOOTER //
// src/components/Miembros/RealizarPago.vue